import {ETIQUETA_TIPO_INSPECCION, ETIQUETA_TIPO_INSPECCION_EN, type CampoComparado, type FilaGex} from '@cnc/contracts';
import type {ReactNode} from 'react';
import {Link, useSearchParams} from 'react-router';
import {useGex} from '../api/aduana';
import {CajaAjustable} from '../componentes/caja-ajustable';
import {ZonaAmpliable} from '../componentes/zona-ampliable';
import {PestanasSeguimiento} from '../componentes/pestanas-seguimiento';
import {SelectorMultiple} from '../componentes/selector-multiple';
import {AYUDA_GEX as A} from '../componentes/ayuda-exportaciones';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {et, tr} from '../i18n/idioma';
import {RangoDeLlegada, RangoDeZarpe} from '../componentes/rango-de-llegada';
import {fecha, numero, textoOGuion} from '../utilidades/formato';

// Seguimiento → GEX · Trámite de aduana: una fila por exportación con lo del
// "GEX FR08" (VUCE, SAE, selectividad de la DIAN, facturas) cruzado con lo que
// la app ya sabe de su logística. Donde la app no tiene el dato se muestra el
// del GEX en cursiva; donde dicen cosas distintas, manda la app y se marca ≠.
export function PaginaGex() {
  const [parametros, setParametros] = useSearchParams();
  const consulta = useGex(parametros);
  const x = consulta.data;
  const cambiar = (clave: string, valor: string) => {
    const nuevos = new URLSearchParams(parametros);
    if (valor) nuevos.set(clave, valor);
    else nuevos.delete(clave);
    if (clave !== 'pagina') nuevos.delete('pagina');
    setParametros(nuevos, {replace: true});
  };
  const pagina = Number(parametros.get('pagina') ?? 1);
  const selector = (clave: keyof typeof A, etiqueta: string, opciones: FilaOpcion[] | undefined, ancho = 'w-44') => (
    <SelectorMultiple
      etiqueta={etiqueta}
      valor={parametros.get(clave) ?? undefined}
      opciones={opciones ?? []}
      onCambiar={v => cambiar(clave, v ?? '')}
      ayuda={A[clave]}
      clase={`flex ${ancho} flex-col gap-1 text-xs text-apagado`}
    />
  );

  return (
    <section className="space-y-4">
      <PestanasSeguimiento />
      <div>
        <h1 className="text-2xl font-semibold text-verde-oscuro">
          {tr('GEX · Trámite de aduana', 'GEX · Customs filing')}
        </h1>
        <p className="text-sm text-apagado">
          {tr(
            'Una fila por exportación con su trámite de aduana (VUCE, SAE, selectividad de la DIAN) y sus facturas, junto a lo que la app sabe de su logística. En cursiva, un dato que solo tiene el GEX; con ≠, donde el GEX dice otra cosa (manda la app). Abre una exportación para ver o editar su trámite.',
            'One row per export with its customs filing (VUCE, SAE, DIAN selectivity) and invoices, next to what the app knows about its logistics. In italics, data only the GEX has; with ≠, where the GEX says something else (the app wins). Open an export to view or edit its filing.',
          )}
        </p>
      </div>

      <ZonaAmpliable titulo={tr('GEX · Trámite de aduana', 'GEX · Customs filing')}>
        <div className="flex flex-wrap items-end gap-3 rounded-lg border border-linea bg-white p-3 text-sm">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-apagado">
              {tr('Buscar', 'Search')}
              <NotaDeAyuda texto={A.buscar} dentroDeEtiqueta />
            </span>
            <input
              type="search"
              defaultValue={parametros.get('buscar') ?? ''}
              onKeyDown={e => e.key === 'Enter' && cambiar('buscar', e.currentTarget.value.trim())}
              onBlur={e =>
                e.currentTarget.value.trim() !== (parametros.get('buscar') ?? '') &&
                cambiar('buscar', e.currentTarget.value.trim())
              }
              placeholder={tr(
                'Exportación, radicado, SAE, factura, booking…',
                'Export, filing, SAE, invoice, booking…',
              )}
              className="w-64 rounded-md border border-linea px-2 py-1"
            />
          </label>
          {selector('anio', tr('Año de zarpe', 'Departure year'), x?.opciones.anios, 'w-36')}
          {selector('exportador', tr('Exportador', 'Exporter'), x?.opciones.exportadores)}
          {selector('selectividad', tr('Selectividad DIAN', 'DIAN selectivity'), x?.opciones.selectividades)}
          {selector('pais', tr('País de destino', 'Destination country'), x?.opciones.paises, 'w-36')}
          {selector('agenciaAduanas', tr('Agencia de aduanas', 'Customs agency'), x?.opciones.agencias)}
          <RangoDeZarpe parametros={parametros} cambiar={cambiar} />
          <RangoDeLlegada parametros={parametros} cambiar={cambiar} />
          <label className="flex items-center gap-2 pb-1">
            <input
              type="checkbox"
              checked={parametros.get('soloParaRevisar') === 'true'}
              onChange={e => cambiar('soloParaRevisar', e.target.checked ? 'true' : '')}
            />
            {tr('Solo para revisar', 'Only to review')}
            <NotaDeAyuda texto={A.soloParaRevisar} dentroDeEtiqueta />
          </label>
          <label className="flex items-center gap-2 pb-1">
            <input
              type="checkbox"
              checked={parametros.get('conDiferencias') === 'true'}
              onChange={e => cambiar('conDiferencias', e.target.checked ? 'true' : '')}
            />
            {tr('Solo con diferencias', 'Only with differences')}
            <NotaDeAyuda texto={A.conDiferencias} dentroDeEtiqueta />
          </label>
          {x && (
            <span className="ml-auto text-apagado">
              {tr(`${numero(x.total)} exportaciones`, `${numero(x.total)} exports`)}
            </span>
          )}
        </div>

        {consulta.isError && <p className="text-sm text-alerta">{consulta.error.message}</p>}
        {x && <Tabla filas={x.elementos} />}

        {x && x.totalPaginas > 1 && (
          <div className="flex items-center justify-end gap-3 text-sm">
            <button
              type="button"
              disabled={pagina <= 1}
              onClick={() => cambiar('pagina', String(pagina - 1))}
              className="rounded-md border border-linea bg-white px-3 py-1 disabled:opacity-40"
            >
              {tr('← Anterior', '← Previous')}
            </button>
            <span className="text-apagado">
              {tr(`Página ${pagina} de ${x.totalPaginas}`, `Page ${pagina} of ${x.totalPaginas}`)}
            </span>
            <button
              type="button"
              disabled={pagina >= x.totalPaginas}
              onClick={() => cambiar('pagina', String(pagina + 1))}
              className="rounded-md border border-linea bg-white px-3 py-1 disabled:opacity-40"
            >
              {tr('Siguiente →', 'Next →')}
            </button>
          </div>
        )}
      </ZonaAmpliable>
    </section>
  );
}

type FilaOpcion = {valor: string; etiqueta: string; cantidad?: number};

const th = 'whitespace-nowrap px-3 py-2 font-semibold';
const td = 'whitespace-nowrap px-3 py-1.5';

function Tabla({filas}: {filas: FilaGex[]}) {
  const tipos = et(ETIQUETA_TIPO_INSPECCION, ETIQUETA_TIPO_INSPECCION_EN);
  const grupos: Array<[string, number]> = [
    [tr('Exportación', 'Export'), 3],
    [tr('Facturas', 'Invoices'), 5],
    ['VUCE', 5],
    [tr('Aduana', 'Customs'), 4],
    [tr('Reserva y embarque', 'Booking and shipment'), 7],
    [tr('Puerto y DIAN', 'Port and DIAN'), 5],
    [tr('Llegada', 'Arrival'), 3],
  ];
  return (
    <CajaAjustable className="overflow-auto rounded-lg border border-linea bg-white">
      <table className="w-full text-sm" data-prueba="gex">
        <thead className="sticky top-0 z-10 bg-white">
          <tr className="border-b border-linea bg-papel text-left text-[11px] uppercase tracking-wide text-apagado">
            {grupos.map(([g, n]) => (
              <th key={g} colSpan={n} className="border-l border-linea px-3 py-1 font-semibold first:border-l-0">
                {g}
              </th>
            ))}
          </tr>
          <tr className="text-left text-xs uppercase tracking-wide text-apagado">
            <th className={th}>
              {tr('Exportación', 'Export')}
              <NotaDeAyuda texto={A.exportacion} />
            </th>
            <th className={th}>
              {tr('Exportador', 'Exporter')}
              <NotaDeAyuda texto={A.exportador} />
            </th>
            <th className={th}>
              {tr('Estado', 'Status')}
              <NotaDeAyuda texto={A.estado} />
            </th>
            <th className={th}>
              {tr('No. EXP', 'Export no.')}
              <NotaDeAyuda texto={A.numeroExportacion} />
            </th>
            <th className={th}>
              {tr('Factura', 'Invoice')}
              <NotaDeAyuda texto={A.facturas} />
            </th>
            <th className={th}>
              {tr('Fecha', 'Date')}
              <NotaDeAyuda texto={A.fechaFactura} />
            </th>
            <th className={`${th} text-right`}>
              {tr('Toneladas', 'Tons')}
              <NotaDeAyuda texto={A.toneladas} />
            </th>
            <th className={`${th} text-right`}>
              {tr('Valor', 'Value')}
              <NotaDeAyuda texto={A.valor} />
            </th>
            <th className={th}>
              {tr('Generación', 'Generated')}
              <NotaDeAyuda texto={A.vuceGeneracion} />
            </th>
            <th className={th}>
              {tr('Pago', 'Paid')}
              <NotaDeAyuda texto={A.vucePago} />
            </th>
            <th className={th}>
              {tr('Radicado', 'Filing no.')}
              <NotaDeAyuda texto={A.vuceRadicado} />
            </th>
            <th className={th}>
              {tr('Aprobación', 'Approval')}
              <NotaDeAyuda texto={A.vuceAprobacion} />
            </th>
            <th className={th}>
              {tr('Fecha aprobación', 'Approval date')}
              <NotaDeAyuda texto={A.vuceFechaAprobacion} />
            </th>
            <th className={th}>
              {tr('País', 'Country')}
              <NotaDeAyuda texto={A.pais} />
            </th>
            <th className={th}>
              {tr('Agencia de aduanas', 'Customs agency')}
              <NotaDeAyuda texto={A.agenciaAduanas} />
            </th>
            <th className={th}>
              {tr('Fecha SAE', 'SAE date')}
              <NotaDeAyuda texto={A.saeFecha} />
            </th>
            <th className={th}>
              {tr('No. SAE', 'SAE no.')}
              <NotaDeAyuda texto={A.saeNumero} />
            </th>
            <th className={th}>
              {tr('Agencia de carga', 'Freight agent')}
              <NotaDeAyuda texto={A.agenciaCarga} />
            </th>
            <th className={th}>
              {tr('Solicitud al forwarder', 'Requested from forwarder')}
              <NotaDeAyuda texto={A.solicitudReserva} />
            </th>
            <th className={th}>
              {tr('Fecha booking', 'Booking date')}
              <NotaDeAyuda texto={A.fechaBooking} />
            </th>
            <th className={th}>
              Booking
              <NotaDeAyuda texto={A.booking} />
            </th>
            <th className={th}>
              {tr('Naviera', 'Shipping line')}
              <NotaDeAyuda texto={A.naviera} />
            </th>
            <th className={th}>
              {tr('Motonave / viaje', 'Vessel / voyage')}
              <NotaDeAyuda texto={A.motonave} />
            </th>
            <th className={`${th} text-right`}>
              {tr('Contenedores', 'Containers')}
              <NotaDeAyuda texto={A.contenedores} />
            </th>
            <th className={th}>
              {tr('Ingreso a puerto', 'Port entry')}
              <NotaDeAyuda texto={A.ingresoPuerto} />
            </th>
            <th className={th}>
              {tr('Puerto', 'Port')}
              <NotaDeAyuda texto={A.puerto} />
            </th>
            <th className={th}>
              {tr('Selectividad', 'Selectivity')}
              <NotaDeAyuda texto={A.selectividad} />
            </th>
            <th className={th}>
              {tr('Inspección', 'Inspection')}
              <NotaDeAyuda texto={A.fechaInspeccion} />
            </th>
            <th className={th}>
              {tr('Zarpe', 'Departure')}
              <NotaDeAyuda texto={A.zarpe} />
            </th>
            <th className={th}>
              {tr('Emisión BL', 'BL issued')}
              <NotaDeAyuda texto={A.emisionBl} />
            </th>
            <th className={th}>
              {tr('Llegada', 'Arrival')}
              <NotaDeAyuda texto={A.llegada} />
            </th>
            <th className={th}>
              {tr('Cliente', 'Customer')}
              <NotaDeAyuda texto={A.cliente} />
            </th>
          </tr>
        </thead>
        <tbody>
          {filas.map(f => {
            const comparado = (campo: CampoComparado, contenido: ReactNode) => (
              <Comparado fila={f} campo={campo}>
                {contenido}
              </Comparado>
            );
            return (
              <tr key={f.exportacion} className="border-t border-linea/70 align-top">
                <td className={td}>
                  <Link
                    to={`/seguimiento/${encodeURIComponent(f.exportacion)}#aduana`}
                    className="font-medium text-verde hover:underline"
                  >
                    {f.exportacion}
                  </Link>
                  {f.paraRevisar && (
                    <span className="ml-1 cursor-help text-xs" title={f.nota ?? undefined}>
                      {tr('⚠ revisar', '⚠ review')}
                    </span>
                  )}
                </td>
                <td className={td}>{textoOGuion(f.exportador)}</td>
                <td className={td}>{textoOGuion(f.estado)}</td>
                <td className={td}>{textoOGuion(f.numeroExportacion)}</td>
                <td className={td}>{textoOGuion(f.facturas)}</td>
                <td className={`${td} tabular-nums`}>{fecha(f.fechaFactura)}</td>
                <td className={`${td} text-right tabular-nums`}>{numero(f.toneladas, 2)}</td>
                <td className={`${td} text-right tabular-nums`}>
                  {f.valorUsd !== null && <div>USD {numero(f.valorUsd, 2)}</div>}
                  {f.valorCop !== null && <div>COP {numero(f.valorCop, 2)}</div>}
                  {f.valorUsd === null && f.valorCop === null && '—'}
                </td>
                <td className={`${td} tabular-nums`}>{fecha(f.vuceGeneracion)}</td>
                <td className={`${td} tabular-nums`}>{fecha(f.vucePago)}</td>
                <td className={td}>{textoOGuion(f.vuceRadicado)}</td>
                <td className={td}>{textoOGuion(f.vuceAprobacion)}</td>
                <td className={`${td} tabular-nums`}>{fecha(f.vuceFechaAprobacion)}</td>
                <td className={td}>{textoOGuion(f.paisDestino)}</td>
                <td className={td}>{textoOGuion(f.agenciaAduanas)}</td>
                <td className={`${td} tabular-nums`}>{fecha(f.saeFecha)}</td>
                <td className={td}>{textoOGuion(f.saeNumero)}</td>
                <td className={td}>{textoOGuion(f.agenciaCarga)}</td>
                <td className={`${td} tabular-nums`}>{fecha(f.solicitudReserva)}</td>
                <td className={`${td} tabular-nums`}>{fecha(f.fechaBooking)}</td>
                <td className={td}>
                  {comparado('booking', textoOGuion(f.booking))}
                  {f.tieneReserva && (
                    <Link
                      to="/seguimiento/reservas"
                      className="ml-1 text-xs text-verde hover:underline"
                      title={tr('Tiene reserva registrada en la app', 'Has a booking registered in the app')}
                    >
                      {tr('reserva', 'booking')}
                    </Link>
                  )}
                </td>
                <td className={td}>{comparado('naviera', textoOGuion(f.naviera))}</td>
                <td className={td}>{comparado('motonave', textoOGuion(f.motonave))}</td>
                <td className={`${td} text-right tabular-nums`}>
                  {f.contenedores > 0 ? (
                    <Link
                      to={`/seguimiento/${encodeURIComponent(f.exportacion)}#logistica`}
                      className="text-verde hover:underline"
                    >
                      {numero(f.contenedores)}
                    </Link>
                  ) : (
                    '—'
                  )}
                </td>
                <td className={`${td} tabular-nums`}>{comparado('ingresoPuerto', fecha(f.ingresoPuerto))}</td>
                <td className={td}>{comparado('puerto', textoOGuion(f.puerto))}</td>
                <td className={td}>
                  {f.selectividad ? tipos[f.selectividad] : '—'}
                  {f.selectividad === 'otro' && f.selectividadTexto && (
                    <span className="text-apagado"> ({f.selectividadTexto})</span>
                  )}
                </td>
                <td className={`${td} tabular-nums`}>{fecha(f.fechaInspeccion)}</td>
                <td className={`${td} tabular-nums`}>{comparado('zarpe', fecha(f.zarpe))}</td>
                <td className={`${td} tabular-nums`}>{comparado('emisionBl', fecha(f.emisionBl))}</td>
                <td className={`${td} tabular-nums`}>{comparado('llegada', fecha(f.llegada))}</td>
                <td className={td}>{comparado('cliente', textoOGuion(f.cliente))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {filas.length === 0 && (
        <p className="p-4 text-sm text-apagado">
          {tr(
            'No hay exportaciones con estos filtros. Si la lista está vacía, falta cargar el GEX.',
            'No exports with these filters. If the list is empty, the GEX has not been loaded yet.',
          )}
        </p>
      )}
    </CajaAjustable>
  );
}

// Un dato que la app y el GEX pueden tener: en cursiva si solo lo trae el GEX;
// con ≠ si el GEX dice otra cosa (se ve en el detalle de la exportacion).
function Comparado({fila, campo, children}: {fila: FilaGex; campo: CampoComparado; children: ReactNode}) {
  if (fila.delGex.includes(campo))
    return (
      <span
        className="italic text-apagado"
        title={tr('Dato del GEX: la app todavía no lo tiene', 'GEX data: the app does not have it yet')}
      >
        {children}
      </span>
    );
  if (fila.diferencias.includes(campo))
    return (
      <span
        title={tr(
          'El GEX dice otra cosa; manda la app. Abre la exportación para ver los dos.',
          'The GEX says something else; the app wins. Open the export to see both.',
        )}
      >
        {children} <span className="font-semibold text-alerta">≠</span>
      </span>
    );
  return <>{children}</>;
}
